import { useEffect, useMemo, useState } from 'react';

import useDevice from 'hooks/useDevice';

import { TEST_ID } from './constants';
import type { DeviceContext, FunctionComponent, Props } from './types';

export const DeviceProvider: FunctionComponent<Props> = ({
    children,
    deskWidthPx = '0px',
    testId = TEST_ID
}) => {
    const { context, devices } = useDevice();

    const sizeMQ = useMemo(() => window.matchMedia(`(min-width: ${deskWidthPx})`), [deskWidthPx]);

    // TODO: come back to this to see if there is a better alternative, since navigator.platform seems to be deprecated
    const isIOS = useMemo(
        () =>
            [
                'iPad Simulator',
                'iPhone Simulator',
                'iPod Simulator',
                'iPad',
                'iPhone',
                'iPod'
            ].includes(navigator.platform) ||
            // iPad on iOS 13 detection
            (navigator.userAgent.includes('Mac') && 'ontouchend' in document),
        []
    );

    const [state, setState] = useState<DeviceContext>({
        ...devices,
        deviceOS: isIOS ? 'ios' : 'other',
        deviceType: sizeMQ.matches ? 'desktop' : 'mobile',
        networkStatus: navigator.onLine ? 'online' : 'offline'
    });

    useEffect(() => {
        const sizeCb = ({ matches }: MediaQueryListEvent) =>
            setState(s => ({
                ...s,
                deviceType: matches ? 'desktop' : 'mobile'
            }));

        const onlineCb = () =>
            setState(currentState => ({
                ...currentState,
                networkStatus: 'online'
            }));

        const offlineCb = () =>
            setState(currentState => ({
                ...currentState,
                networkStatus: 'offline'
            }));

        sizeMQ.addEventListener('change', sizeCb);
        window.addEventListener('online', onlineCb);
        window.addEventListener('offline', offlineCb);

        return () => {
            sizeMQ.removeEventListener('change', sizeCb);
            window.removeEventListener('online', onlineCb);
            window.removeEventListener('offline', offlineCb);
        };
    }, [sizeMQ, setState]);

    return (
        <context.Provider data-testid={testId} value={state}>
            {children}
        </context.Provider>
    );
};
